import React, { useRef, useState } from "react";
import {
    Box,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { EmojiEmotions } from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import { waCreateMenu, waUpdateMenu } from "../wa/api/wa_menu_Api";
import { useSnackbar } from "notistack";

// 👉 Inserta emoji en el input o textarea en la posición del cursor
function insertEmojiAtInput(input, emoji) {
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const before = input.value.slice(0, start);
    const after = input.value.slice(end);
    const next = before + emoji + after;
    input.value = next;
    const caret = start + emoji.length;
    return { value: next, caret };
}

export default function WaMenuEditor({ menu, onSave }) {
    const { enqueueSnackbar } = useSnackbar();
    const [nombre, setNombre] = useState(menu?.nombre || "");
    const [titulo, setTitulo] = useState(menu?.titulo || "");
    const [picker, setPicker] = useState({ open: false, field: null });

    const [descripcion, setDescripcion] = useState(menu?.descripcion || "");
    const [disponible, setDisponible] = useState(menu?.disponible ?? true);

    // Refs a los inputs
    const refNombre = useRef();
    const refTitulo = useRef();

    const openPicker = (field) => setPicker({ open: true, field });
    const closePicker = () => setPicker({ open: false, field: null });

    const onEmojiClick = (emojiData) => {
        const field = picker.field;
        const input = field === "nombre" ? refNombre.current : refTitulo.current;
        if (!input) return closePicker();

        const inserted = insertEmojiAtInput(input, emojiData.emoji);
        if (!inserted) return closePicker();

        if (field === "nombre") setNombre(inserted.value);
        else setTitulo(inserted.value);

        setTimeout(() => {
            input.focus();
            if (typeof inserted.caret === "number")
                input.setSelectionRange(inserted.caret, inserted.caret);
        }, 0);

        closePicker();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            nombre,
            titulo,
            descripcion,
            disponible,
            activo: 1,
        };

        if (menu) await waUpdateMenu(menu.id, payload);
        else await waCreateMenu(payload);
        enqueueSnackbar("✅ Menú guardado correctamente", { variant: "success" });
        onSave();
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {/* Campo: Nombre interno */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TextField
                    label="Nombre interno"
                    inputRef={refNombre}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    fullWidth
                    required
                />
                <IconButton
                    onClick={() => openPicker("nombre")}
                    title="Agregar emoji al nombre"
                >
                    <EmojiEmotions fontSize="small" />
                </IconButton>
            </Box>

            {/* Campo: Título o mensaje del menú */}
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                <TextField
                    label="Título o mensaje del menú"
                    inputRef={refTitulo}
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    required
                />
                <IconButton
                    onClick={() => openPicker("titulo")}
                    title="Agregar emoji al título"
                    sx={{ mt: 1 }}
                >
                    <EmojiEmotions fontSize="small" />
                </IconButton>
            </Box>

            <TextField
                label="Descripción / mensaje del menú"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={disponible}
                        onChange={(e) => setDisponible(e.target.checked)}
                    />
                }
                label="Disponible en el bot"
            />

            <Button type="submit" variant="contained">
                Guardar
            </Button>

            {/* Picker de emojis en modal */}
            <Dialog open={picker.open} onClose={closePicker} maxWidth="xs" fullWidth>
                <DialogTitle>Seleccioná un emoji</DialogTitle>
                <DialogContent>
                    <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        searchDisabled={false}
                        previewConfig={{ showPreview: false }}
                        height={360}
                        width="100%"
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
}
